import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Upload, AlertCircle, Play, Square, RefreshCw, CheckCircle, FileText, ArrowLeft, Database, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { usePermission } from '../hooks/usePermission';
import { AccessLevel } from '../types';

interface LogEntry {
  type: 'info' | 'success' | 'warn' | 'error';
  message: string;
  time: string;
}

interface UpdateItem {
  id: string;
  id_istat: string;
  latitudine: number | null;
  longitudine: number | null;
  cap: string[];
}

export const ImportComuniCap: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();

  const hasAccess = hasPermission('app.sistema.configurazione', AccessLevel.ro);

  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center bg-white rounded-3xl border border-slate-100 shadow-xs max-w-2xl mx-auto my-12">
        <div id="no-access-import-icon-wrapper" className="w-16 h-16 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center mb-4 border border-red-100/50">
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 15v2m0-6v4m0-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h1 className="text-xl font-black text-slate-800 uppercase tracking-tight">Accesso non consentito</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-sm font-semibold">
          Don't have permission to access. È richiesto l'inserimento nel gruppo <code className="bg-slate-50 border border-slate-100 px-1.5 py-0.5 rounded-lg font-mono text-xs text-red-600 font-bold">app.sistema.configurazione</code>.
        </p>
      </div>
    );
  }

  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // State
  const [file, setFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<string[][]>([]);
  const [totalLines, setTotalLines] = useState(0);
  const [status, setStatus] = useState<'idle' | 'reading' | 'ready' | 'processing' | 'completed' | 'stopped' | 'error'>('idle');
  
  const [matchingCount, setMatchingCount] = useState(0);
  const [processedCount, setProcessedCount] = useState(0);
  const [successCount, setSuccessCount] = useState(0);
  const [failCount, setFailCount] = useState(0);
  
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  
  const stopRequestedRef = useRef(false);
  const logContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll logs
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  const addLog = (message: string, type: LogEntry['type'] = 'info') => {
    const time = new Date().toLocaleTimeString();
    setLogs((prev) => [...prev, { type, message, time }]);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      processFile(droppedFile);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      processFile(selectedFile);
    }
  };

  const processFile = (selectedFile: File) => {
    if (!selectedFile.name.endsWith('.csv')) {
      toast.error('Il file deve essere in formato CSV (.csv)');
      return;
    }
    setFile(selectedFile);
    setStatus('reading');
    setLogs([]);
    addLog(`File selezionato: ${selectedFile.name} (${(selectedFile.size / 1024).toFixed(1)} KB)`);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split(/\r?\n/).filter(line => line.trim().length > 0);
        setTotalLines(lines.length - 1); // Exclude header

        // Generate small preview (first 5 rows)
        const previewRows: string[][] = [];
        const limit = Math.min(lines.length, 6);
        for (let i = 0; i < limit; i++) {
          previewRows.push(parseCSVRow(lines[i]));
        }
        setCsvPreview(previewRows);
        setStatus('ready');
        addLog(`Analisi del file completata. Trovate ${lines.length - 1} righe di dati da elaborare.`, 'success');
      } catch (err: any) {
        setStatus('error');
        addLog(`Errore nella lettura del file: ${err.message}`, 'error');
        toast.error('Errore nella lettura del file');
      }
    };
    reader.readAsText(selectedFile, 'UTF-8');
  };

  // Helper parsing function for CSV semicolon separated
  const parseCSVRow = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ';' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const startImport = async () => {
    if (!file) return;

    setStatus('processing');
    stopRequestedRef.current = false;
    setProcessedCount(0);
    setSuccessCount(0);
    setFailCount(0);
    addLog('Avvio processo di importazione e allineamento dati...', 'info');

    try {
      // 1. Fetch ALL existing municipality IDs in Database to check matches
      addLog('Verifica comuni esistenti nel database...', 'info');
      let allExistingIds: string[] = [];
      let hasMore = true;
      let page = 0;
      const pageSize = 1000;

      while (hasMore) {
        if (stopRequestedRef.current) break;
        const { data, error } = await supabase
          .from('cpgev_comuni')
          .select('id')
          .range(page * pageSize, (page + 1) * pageSize - 1);

        if (error) {
          throw new Error(`Errore caricamento comuni dal database: ${error.message}`);
        }
        if (!data || data.length === 0) {
          hasMore = false;
        } else {
          allExistingIds.push(...data.map(d => d.id));
          page++;
          if (data.length < pageSize) hasMore = false;
        }
      }

      if (stopRequestedRef.current) {
        setStatus('stopped');
        addLog('Processo interrotto dall\'utente', 'warn');
        return;
      }

      const existingSet = new Set(allExistingIds);
      addLog(`Trovati ${existingSet.size} comuni nel database.`, 'info');

      // 2. Read file again to parse all lines
      addLog('Inizio raggruppamento e processing delle righe CSV...', 'info');
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split(/\r?\n/).filter(line => line.trim().length > 0);
          
          if (lines.length < 2) {
            throw new Error('Il file CSV non contiene righe di dati oltre alle intestazioni.');
          }

          const headers = parseCSVRow(lines[0]);
          const idxBelfiore = headers.indexOf('codice_belfiore');
          const idxIstat = headers.indexOf('codice_istat');
          const idxCap = headers.indexOf('cap');
          const idxLat = headers.indexOf('lat');
          const idxLon = headers.indexOf('lon');
          const idxDenom = headers.indexOf('denominazione_ita');

          if (idxBelfiore === -1 || idxIstat === -1 || idxCap === -1) {
            throw new Error('Intestazioni CSV non valide. Devono esserci almeno "codice_belfiore", "codice_istat" e "cap"');
          }

          // Group by belfiore to aggregate all CAP values per belfiore code
          const mapBelfioreToCaps = new Map<string, Set<string>>();
          const mapBelfioreToIstat = new Map<string, string>();
          const mapBelfioreToLat = new Map<string, number | null>();
          const mapBelfioreToLon = new Map<string, number | null>();
          const mapBelfioreToDenom = new Map<string, string>();

          for (let i = 1; i < lines.length; i++) {
            const cells = parseCSVRow(lines[i]);
            if (cells.length < headers.length) continue;

            const belfiore = cells[idxBelfiore];
            if (!belfiore) continue;

            const istat = cells[idxIstat];
            const capVal = cells[idxCap];
            const latStr = cells[idxLat]?.replace(',', '.');
            const lonStr = cells[idxLon]?.replace(',', '.');
            const denom = cells[idxDenom] || belfiore;

            // Group cap codes
            if (!mapBelfioreToCaps.has(belfiore)) {
              mapBelfioreToCaps.set(belfiore, new Set<string>());
            }
            if (capVal) {
              mapBelfioreToCaps.get(belfiore)!.add(capVal);
            }

            if (istat) {
              mapBelfioreToIstat.set(belfiore, istat);
            }
            
            const lat = latStr ? parseFloat(latStr) : null;
            if (lat && !isNaN(lat)) {
              mapBelfioreToLat.set(belfiore, lat);
            }

            const lon = lonStr ? parseFloat(lonStr) : null;
            if (lon && !isNaN(lon)) {
              mapBelfioreToLon.set(belfiore, lon);
            }

            mapBelfioreToDenom.set(belfiore, denom);
          }

          // Build list of items to update which are also present in the database cpgev_comuni
          const itemsToUpdate: UpdateItem[] = [];
          for (const belfiore of mapBelfioreToCaps.keys()) {
            if (existingSet.has(belfiore)) {
              itemsToUpdate.push({
                id: belfiore,
                id_istat: mapBelfioreToIstat.get(belfiore) || '',
                latitudine: mapBelfioreToLat.get(belfiore) || null,
                longitudine: mapBelfioreToLon.get(belfiore) || null,
                cap: Array.from(mapBelfioreToCaps.get(belfiore)!)
              });
            }
          }

          const itemsCount = itemsToUpdate.length;
          setMatchingCount(itemsCount);
          addLog(`Incrocio completato: ${itemsCount} comuni del CSV corrispondono nel database.`, 'info');

          if (itemsCount === 0) {
            setStatus('completed');
            addLog('Fatto. Nessun comune corrispondente trovato per l\'aggiornamento.', 'warn');
            return;
          }

          // Core worker loop in batches
          const chunkSize = 25; // Chunk size for concurrent API requests
          addLog(`Esecuzione aggiornamenti in parallelo (chunk size: ${chunkSize})...`, 'info');

          for (let j = 0; j < itemsToUpdate.length; j += chunkSize) {
            if (stopRequestedRef.current) {
              setStatus('stopped');
              addLog('Procedura interrotta dall\'utente.', 'warn');
              return;
            }

            const chunk = itemsToUpdate.slice(j, j + chunkSize);
            let chunkSuccess = 0;
            let chunkFail = 0;

            await Promise.all(chunk.map(async (item) => {
              if (stopRequestedRef.current) return;
              try {
                const { error: updateError } = await supabase
                  .from('cpgev_comuni')
                  .update({
                    id_istat: item.id_istat,
                    latitudine: item.latitudine,
                    longitudine: item.longitudine,
                    cap: item.cap,
                    data_aggiornamento: new Date().toISOString()
                  })
                  .eq('id', item.id);

                if (updateError) {
                  chunkFail++;
                  const name = mapBelfioreToDenom.get(item.id) || item.id;
                  console.error(`Errore aggiornamento comune ${item.id} (${name}):`, updateError);
                  addLog(`Errore comune ${item.id} (${name}): ${updateError.message}`, 'error');
                } else {
                  chunkSuccess++;
                }
              } catch (ex: any) {
                chunkFail++;
                addLog(`Eccezione comune ${item.id}: ${ex.message || ex}`, 'error');
              }
            }));

            // Sync counters to keep UI animated and reactive
            setProcessedCount(prev => prev + chunk.length);
            setSuccessCount(prev => prev + chunkSuccess);
            setFailCount(prev => prev + chunkFail);
          }

          setStatus('completed');
          addLog('Processo di importazione completato con successo!', 'success');
          toast.success('Importazione completata con successo!');
        } catch (csvErr: any) {
          setStatus('error');
          console.error('[IMPORT_COMUNI] Errore CSV:', csvErr);
          addLog(`Errore nel thread di elaborazione: ${csvErr.message || csvErr}`, 'error');
          toast.error(`Errore: ${csvErr.message}`);
        }
      };

      reader.readAsText(file, 'UTF-8');

    } catch (e: any) {
      setStatus('error');
      console.error('[IMPORT_COMUNI] Eccezione esterna:', e);
      addLog(`Errore fatale: ${e.message || e}`, 'error');
      toast.error('Errore durante la preparazione del database');
    }
  };

  const handleStop = () => {
    stopRequestedRef.current = true;
    setStatus('stopped');
    addLog('Richiesta di interruzione ricevuta. Arresto in corso...', 'warn');
  };

  const resetPage = () => {
    setFile(null);
    setCsvPreview([]);
    setTotalLines(0);
    setStatus('idle');
    setMatchingCount(0);
    setProcessedCount(0);
    setSuccessCount(0);
    setFailCount(0);
    setLogs([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="pb-10 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/sistema/configurazione')}
          className="p-2 bg-white hover:bg-slate-100 rounded-xl border border-slate-200 transition-colors"
          title="Torna a configurazioni"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Importazione Comuni e CAP</h1>
          <p className="text-slate-500 text-sm">Aggiorna la tabella cpgev_comuni tramite file gi_comuni_cap.csv</p>
        </div>
      </div>

      {/* Logic info panel */}
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-3">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
          <Database className="w-4 h-4 text-primary" />
          Regole di allineamento e mappatura
        </h3>
        <ul className="text-sm text-slate-600 space-y-1.5 list-disc pl-5">
          <li><strong>Chiave di corrispondenza:</strong> Il codice Belfiore del CSV <code className="bg-slate-200 px-1 py-0.5 rounded text-xs font-mono">codice_belfiore</code> corrisponde al campo <code className="bg-slate-200 px-1 py-0.5 rounded text-xs font-mono">id</code> della tabella <code className="bg-slate-200 px-1 py-0.5 rounded text-xs">cpgev_comuni</code>.</li>
          <li><strong>Aggiornamento campi:</strong> Vengono scritti i campi <code className="bg-slate-200 px-1... rounded text-xs">id_istat</code>, <code className="bg-slate-200 px-1... rounded text-xs">latitudine</code> e <code className="bg-slate-200 px-1... rounded text-xs">longitudine</code>.</li>
          <li><strong>Aggregazione CAP:</strong> Se ci sono più CAP nel CSV per lo stesso codice Belfiore, vengono inseriti tutti in un array <code className="bg-slate-200 px-1 py-0.5 rounded text-xs font-mono">character varying(5)[]</code>.</li>
          <li>I numeri di latitudine e longitudine in formato italiano (es. <code className="bg-slate-200 px-1 rounded text-xs">45,359</code>) vengono normalizzati automaticamente in float con punto decimale.</li>
        </ul>
      </div>

      {/* Drag & Drop Field */}
      {status === 'idle' && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-3xl p-10 cursor-pointer text-center transition-all ${
            isDragOver ? 'border-primary bg-primary/5 shadow-inner' : 'border-slate-300 hover:border-slate-400 bg-white shadow-sm'
          }`}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".csv"
            className="hidden"
          />
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
              <Upload className="w-8 h-8" />
            </div>
            <div>
              <p className="text-base font-semibold text-slate-800">Trascina qui il file gi_comuni_cap.csv</p>
              <p className="text-sm text-slate-400 mt-1">oppure clicca per sceglierlo dal computer</p>
            </div>
          </div>
        </div>
      )}

      {/* Upload reading animation */}
      {status === 'reading' && (
        <div className="bg-white border rounded-3xl p-10 text-center flex flex-col items-center gap-4">
          <RefreshCw className="w-10 h-10 animate-spin text-primary" />
          <p className="text-slate-600 font-medium">Lettura e analisi del file in corso...</p>
        </div>
      )}

      {/* File Loaded and Ready */}
      {status !== 'idle' && status !== 'reading' && (
        <div className="bg-white border border-slate-200 rounded-3xl p-6 space-y-6 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-teal-50 rounded-2xl text-teal-600 border border-teal-100">
                <FileText className="w-6 h-6" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-800">{file?.name}</h4>
                <p className="text-xs text-slate-400">Dimensione: {(file ? file.size / 1024 : 0).toFixed(1)} KB | Righe: {totalLines}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {status === 'ready' && (
                <button
                  onClick={startImport}
                  className="px-5 py-2.5 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary/90 flex items-center gap-2 shadow-sm transition-all"
                >
                  <Play className="w-4 h-4 fill-white" />
                  Avvia Importazione
                </button>
              )}
              {status === 'processing' && (
                <button
                  onClick={handleStop}
                  className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-xl flex items-center gap-2 shadow-sm transition-all"
                >
                  <Square className="w-4 h-4 fill-white" />
                  Interrompi
                </button>
              )}
              {(status === 'completed' || status === 'stopped' || status === 'error') && (
                <button
                  onClick={resetPage}
                  className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl flex items-center gap-2 transition-all border border-slate-200"
                >
                  <Trash2 className="w-4 h-4" />
                  Rimuovi File
                </button>
              )}
            </div>
          </div>

          {/* Progress Indicators */}
          {status !== 'ready' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-50 border border-slate-100 p-4 rounded-2xl text-center">
                  <span className="text-[10px] text-slate-400 font-bold uppercase block">Incrocio DB</span>
                  <span className="text-xl font-bold text-slate-700">{matchingCount}</span>
                </div>
                <div className="bg-slate-50 border border-slate-100 p-4 rounded-2xl text-center">
                  <span className="text-[10px] text-slate-400 font-bold uppercase block">Elaborati</span>
                  <span className="text-xl font-bold text-slate-700">{processedCount} / {matchingCount}</span>
                </div>
                <div className="bg-emerald-50 border border-emerald-100 p-4 rounded-2xl text-center">
                  <span className="text-[10px] text-emerald-600 font-bold uppercase block">Aggiornati</span>
                  <span className="text-xl font-bold text-emerald-700">{successCount}</span>
                </div>
                <div className="bg-rose-50 border border-rose-100 p-4 rounded-2xl text-center">
                  <span className="text-[10px] text-rose-500 font-bold uppercase block">Falliti</span>
                  <span className="text-xl font-bold text-rose-700">{failCount}</span>
                </div>
              </div>

              {/* Progress Bar */}
              {matchingCount > 0 && (
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs font-semibold text-slate-500">
                    <span>Avanzamento</span>
                    <span>{Math.round((processedCount / matchingCount) * 100)}%</span>
                  </div>
                  <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${status === 'completed' ? 'bg-emerald-500' : 'bg-primary'}`}
                      style={{ width: `${(processedCount / matchingCount) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Quick Preview of parsed CSV data */}
          {status === 'ready' && csvPreview.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-xs font-bold text-slate-400 uppercase tracking-wide">Anteprima del CSV (Prime righe)</h5>
              <div className="overflow-x-auto border border-slate-100 rounded-xl">
                <table className="w-full text-left border-collapse text-xs font-medium">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500 border-b border-slate-100 font-bold">
                      <th className="p-2.5">Codice Belfiore</th>
                      <th className="p-2.5">Istat</th>
                      <th className="p-2.5">Comune</th>
                      <th className="p-2.5 font-mono">CAP</th>
                      <th className="p-2.5">Lat</th>
                      <th className="p-2.5">Lon</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-150">
                    {csvPreview.slice(1).map((row, i) => (
                      <tr key={i} className="hover:bg-slate-50/50">
                        <td className="p-2.5 font-semibold text-slate-700">{row[13] || '-'}</td>
                        <td className="p-2.5 text-slate-500">{row[0] || '-'}</td>
                        <td className="p-2.5 text-slate-800">{row[2] || '-'}</td>
                        <td className="p-2.5 font-mono text-slate-600">{row[4] || '-'}</td>
                        <td className="p-2.5 text-slate-500">{row[14] || '-'}</td>
                        <td className="p-2.5 text-slate-500">{row[15] || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Console / Log panel */}
          <div className="space-y-2">
            <h5 className="text-xs font-bold text-slate-400 uppercase tracking-wide flex items-center justify-between">
              <span>Log del Processo</span>
              {logs.length > 0 && (
                <button
                  onClick={() => setLogs([])}
                  className="text-[10px] text-slate-400 hover:text-slate-600 cursor-pointer"
                >
                  Svuota console
                </button>
              )}
            </h5>
            <div
              ref={logContainerRef}
              className="bg-slate-900 text-slate-100 p-4 rounded-xl font-mono text-xs h-60 overflow-y-auto space-y-1 scrollbar-thin scrollbar-thumb-slate-700"
            >
              {logs.length === 0 ? (
                <p className="text-slate-500 italic">In attesa di eventi...</p>
              ) : (
                logs.map((log, index) => (
                  <div key={index} className="flex gap-2">
                    <span className="text-slate-500 shrink-0 select-none">[{log.time}]</span>
                    <span
                      className={
                        log.type === 'success'
                          ? 'text-emerald-400'
                          : log.type === 'error'
                          ? 'text-rose-400'
                          : log.type === 'warn'
                          ? 'text-amber-400'
                          : 'text-slate-200'
                      }
                    >
                      {log.message}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
